// src/components/HelpView.jsx
/* eslint-disable i18next/no-literal-string, react/jsx-no-literals */
import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import codeVaultImg from '../../../docs/screenshots/screenshot_01_code_vault.png';
import aiVaultImg from '../../../docs/screenshots/screenshot_02_ai_vault.png';
import linksVaultImg from '../../../docs/screenshots/screenshot_03_links_vault.png';
import toolsVaultImg from '../../../docs/screenshots/screenshot_04_tools_vault.png';

const HelpView = ({ setView, theme }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center gap-3 border-b border-gray-800 pb-3 shrink-0">
        <button onClick={() => setView('home')} className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition">
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-white font-bold text-lg">Documentation & Guide</h2>
      </div>

      <div className="grow overflow-y-auto custom-scrollbar pr-2 space-y-6 text-sm text-gray-300 pb-4">
        <section>
          <h1 className={`text-2xl font-bold text-white mb-2 flex items-center gap-2`}><span className={theme.textPrimary}>✂️</span> SnipIT</h1>
          <p className="italic text-gray-400 border-l-2 border-gray-700 pl-3 py-1 mb-3">
            A persistent, quad-vault Chrome Side Panel extension engineered to manage, format, and web-clip traditional code snippets, advanced AI context prompts, developer links, and utility tools.
          </p>
        </section>


        <section>
          <h2 className="text-lg font-bold text-white border-b border-gray-800 pb-1 mb-2">📖 User Guide</h2>
          <p className="mb-3 text-gray-400">SnipIT lives directly in your Chrome Side Panel, keeping your code library and AI engineering prompts accessible right alongside your active web pages without disappearing when you click away.</p>

          <h3 className="font-bold text-white mt-4 mb-1">🌓 The Quad-Vault System</h3>
          <p className="mb-2 text-gray-400">At the top of the app, toggle between the <strong>Code Vault</strong>, the <strong>AI Vault</strong>, the <strong>Links Vault</strong>, and the <strong>Tools Vault</strong>. These are entirely separate environments.</p>

          <h3 className="font-bold text-white mt-4 mb-1">💻 Using the Code Vault</h3>
          <img src={codeVaultImg} alt="Code Vault UI" className="w-full rounded border border-gray-800 shadow-md mb-2" />
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>Extract:</strong> Highlight code on any webpage, open the editor, and click "+ Extract highlighted text".</li>
            <li><strong>Smart Format:</strong> Paste messy code and click Format. SnipIT will automatically strip leading <code>=</code> signs, fix indentation, and pair arguments.</li>
            <li><strong>Smart Paste:</strong> Click the Clipboard icon on any snippet card to instantly inject the code into the text box you are currently typing in.</li>
          </ul>

          <h3 className="font-bold text-white mt-4 mb-1">✨ Using the AI Vault</h3>
          <img src={aiVaultImg} alt="AI Vault UI" className="w-full rounded border border-gray-800 shadow-md mb-2" />
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>Prompt:</strong> A standard text area with formatting and tone dropdowns.</li>
            <li><strong>Gem:</strong> A structured 4-part form (Premise, Persona, Context, Rules) that auto-compiles into formatted Markdown.</li>
            <li><strong>System Prompt:</strong> Features a visual warning border and a Temperature slider for global guardrails.</li>
            <li><strong>Few-Shot:</strong> Features an input for defining the Pattern Template, and a large area for example pairs.</li>
            <li><strong>Context:</strong> Includes a dedicated input for Source URLs alongside raw data.</li>
          </ul>

          <h3 className="font-bold text-white mt-4 mb-1">🔗 Using the Links Vault</h3>
          <img src={linksVaultImg} alt="Links Vault UI" className="w-full rounded border border-gray-800 shadow-md mb-2" />
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>Smart Grab:</strong> Click "+ Grab current page URL" to automatically pull the URL and Title of the tab you are looking at.</li>
            <li><strong>Launch:</strong> Click the external link icon on the Home screen to open the URL directly.</li>
          </ul>

          <h3 className="font-bold text-white mt-4 mb-1">🤖 Gemini AI Code Review & Generation</h3>
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>✨ Inline Generator:</strong> Click the "Generate with AI" button directly inside the Code Editor to instantly write boilerplate or complex algorithms.</li>
            <li><strong>🕵️ Code Reviewer:</strong> Click the "AI Review" button on any saved snippet to enter a focused, distraction-free environment that automatically scans your code for bugs, vulnerabilities, and best practices.</li>
            <li><strong>{t('Rich Reports:')}</strong> {t('View actionable markdown reports with remediation advice directly within the application.')}</li>
          </ul>

          <h3 className="font-bold text-white mt-4 mb-1">🌍 Code Gallery & Dev Board</h3>
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>{t('Public Gallery:')}</strong> Discover, view, and instantly copy snippets shared by the community.</li>
            <li><strong>{t('Dev Board:')}</strong> Engage in discussions, ask questions, and share guides in a real-time markdown forum.</li>
          </ul>

          <h3 className="font-bold text-white mt-4 mb-1">🏢 Teams, Billing, & Downloads</h3>
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>{t('Organizations:')}</strong> Create private workspaces and securely share snippets with team members (Pro).</li>
            <li><strong>{t('Billing:')}</strong> Manage Pro subscriptions seamlessly via Stripe Checkout.</li>
            <li><strong>{t('Downloads:')}</strong> Grab the latest <code>{t('snipit-desktop-setup.msi')}</code> installer for the Native Windows app directly from the Web Portal.</li>
          </ul>

          <h3 className="font-bold text-white mt-4 mb-1">🧰 Using the Tools Vault</h3>
          <img src={toolsVaultImg} alt="Tools Vault UI" className="w-full rounded border border-gray-800 shadow-md mb-2" />
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>Regex Tester:</strong> Test expressions against text and copy the pattern with custom wrappers.</li>
            <li><strong>JWT Decoder:</strong> Decode JSON Web Tokens offline to view headers and payloads safely.</li>
            <li><strong>JSON Formatter:</strong> Instantly prettify minified or raw JSON strings.</li>
            <li><strong>Boilerplate Gen:</strong> Generate starter files for common frameworks and structures.</li>
            <li><strong>Mock Data Gen:</strong> Generate realistic test data and export it as JSON, CSV, SQL (Postgres/BigQuery), or Plain Text. Use "Preview as Table" for visual inspection.</li>
            <li><strong>Color Converter:</strong> Use the interactive visual color picker or type #hex/rgb codes to bi-directionally sync formats.</li>
            <li><strong>Markdown Pad:</strong> Write raw markdown and instantly preview it fully rendered with rich typography.</li>
            <li><strong>Base64 & URL Converter:</strong> Encode/decode Base64 strings and escape/unescape URI components.</li>
            <li><strong>Case Converter:</strong> Instantly switch string formats to camelCase, snake_case, PascalCase, etc.</li>
            <li><strong>Hash Generator:</strong> Create secure cryptographic hashes (MD5, SHA-256, etc) completely offline.</li>
          </ul>

          <div className="mt-3 p-3 bg-blue-900/20 border border-blue-800 rounded-md">
            <p className="text-xs text-blue-300">
              💡 <strong>Want a deeper dive?</strong> Check out the{' '}
              <button 
                onClick={() => setView('tools_guide')}
                className="bg-blue-900/40 px-2 py-0.5 rounded text-blue-200 hover:bg-blue-800 hover:text-white transition cursor-pointer font-semibold"
              >
                SnipIT Tools Guide
              </button>{' '}
              for advanced use cases, workflows, and detailed screenshots of these 10 offline utilities!
            </p>
          </div>

          <h3 className="font-bold text-white mt-4 mb-1">🏷️ Organization & Hotkeys</h3>
          <ul className="list-disc pl-5 space-y-1 text-gray-400">
            <li><strong>Edit & Resize:</strong> Click the Title of any snippet to open the editor. Drag the bottom-right corner of text boxes to resize.</li>
            <li><strong>Tags & Tabs:</strong> Add custom <code>#tags</code>. Use the "+ Tab" button to pin specific languages.</li>
            <li><strong>Global Hotkeys:</strong> <code>Ctrl+K</code> (Search), <code>Ctrl+N</code> (New), <code>Ctrl+S</code> (Save), <code>Esc</code> (Home).</li>
            <li><strong>Backups:</strong> Use the 📥 and 📤 icons to import/export your database as a <code>.json</code> file.</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default HelpView;